// @file Vuex store for handling library plans
import { calculateLibraryPlanTotalPrice } from 'bits/billing_helper'
import { captureFetchException } from 'bits/error_tracker'
import { translatePrice } from 'bits/intl'
import * as PadletApi from 'dashboard/padlet_api'
import type { LibraryId, LibraryPlan } from 'types'
import { Module } from 'vuex'

enum UpgradeStep {
  ChooseTier,
  ChooseTierPadletQuota,
  ChooseTierFileSizeQuota,
  ScheduleTeamGold,
}

interface LibraryPlansState {
  plans: HashedLibraryPlans
  xUpgradeModal: boolean
  upgradeStep: UpgradeStep | null
  makersCount: number | null
  wallsUsed: number | null
}

interface HashedLibraryPlan {
  annual: LibraryPlan
  monthly: LibraryPlan
}

type HashedLibraryPlans = Record<'gold', HashedLibraryPlan>

const DEFAULT_PLANS = (): HashedLibraryPlans => ({
  gold: { annual: {} as LibraryPlan, monthly: {} as LibraryPlan },
})

const initialState = (): LibraryPlansState => {
  return {
    plans: DEFAULT_PLANS(),
    xUpgradeModal: false,
    upgradeStep: null,
    makersCount: null,
    wallsUsed: null,
  }
}

const LibraryPlansModule: Module<LibraryPlansState, object> = {
  namespaced: true,
  state: initialState(),
  getters: {
    plans: (state): HashedLibraryPlans => state.plans,
    plansFetched: (state): boolean => Object.keys(state.plans.gold.annual).length !== 0,
    xUpgradeModal: (state): boolean => state.xUpgradeModal,
    upgradeStep: (state): UpgradeStep | null => state.upgradeStep,
    translatedPlanPrice:
      (_, getters) =>
      ({ tier, period, makersCount }: { tier: string; period: string; makersCount: number }): string | null => {
        const plan = getters.plans[tier][period]
        if (!plan.price || !plan.currency) return null
        return translatePrice(
          calculateLibraryPlanTotalPrice({
            makersCount,
            price: parseFloat(plan.price),
          }),
          plan.currency,
        )
      },
    translatedPlanAnnualDiscount:
      (_, getters) =>
      ({ tier, makersCount }: { tier: string; makersCount: number }): string | null => {
        const monthlyPlan = getters.plans[tier]['monthly']
        const annualPlan = getters.plans[tier]['annual']
        if (!monthlyPlan.price || !monthlyPlan.currency || !annualPlan.price || !annualPlan.currency) return null
        const monthlyPlanTotalPrice = calculateLibraryPlanTotalPrice({
          makersCount,
          price: parseFloat(monthlyPlan.price),
        })
        const annualPlanTotalPrice = calculateLibraryPlanTotalPrice({
          makersCount,
          price: parseFloat(annualPlan.price),
        })
        return translatePrice(monthlyPlanTotalPrice * 12 - annualPlanTotalPrice, monthlyPlan.currency)
      },
    makersCount: (state): number | null => state.makersCount,
    wallsUsed: (state): number | null => state.wallsUsed,
  },
  mutations: {
    FETCHED_PLANS(state, plans: LibraryPlan[]): void {
      // Convert a list to a hash that is easy to use in our component
      const hashedPlans = {
        gold: { annual: {} as LibraryPlan, monthly: {} as LibraryPlan },
      }
      for (const plan of plans) {
        plan.formattedPrice = translatePrice(parseFloat(plan.price), plan.currency)
        hashedPlans[plan.tier][plan.period] = plan
      }
      state.plans = hashedPlans
    },
    RESET_PLANS(state): void {
      state.plans = DEFAULT_PLANS()
    },
    SHOW_UPGRADE_MODAL(state, upgradeStep: UpgradeStep): void {
      state.xUpgradeModal = true
      state.upgradeStep = upgradeStep
    },
    CLOSE_UPGRADE_MODAL(state): void {
      state.xUpgradeModal = false
      state.upgradeStep = null
    },
    SET_UPGRADE_MODAL_STEP(state, upgradeStep: UpgradeStep): void {
      state.upgradeStep = upgradeStep
    },
    SET_MAKERS_COUNT(state, makersCount: number | null): void {
      state.makersCount = makersCount
    },
    SET_WALLS_USED(state, wallsUsed: number | null): void {
      state.wallsUsed = wallsUsed
    },
  },
  actions: {
    initialize({ dispatch }, { libraryId }: { libraryId?: string }): void {
      dispatch('fetchPlans', { libraryId })
    },
    async fetchPlans({ commit }, { libraryId }: { libraryId?: string }): Promise<void> {
      try {
        const response = libraryId
          ? await PadletApi.LibraryPlan.fetchByLibraryId({ libraryId })
          : await PadletApi.LibraryPlan.fetch()
        commit('FETCHED_PLANS', response.data)
      } catch (e) {
        captureFetchException(e, { source: 'LibraryPlansFetchPlans' })
      }
    },
    fetchPlansAndShowUpgradeModal(
      { dispatch },
      {
        upgradeStep,
        libraryId,
        makersCount,
        wallsUsed,
      }: { upgradeStep: UpgradeStep; libraryId: LibraryId; makersCount: number; wallsUsed: number },
    ): void {
      dispatch('resetUpgradeModalState')
      dispatch('fetchPlans', { libraryId })
      dispatch('showUpgradeModal', { upgradeStep, makersCount, wallsUsed })
    },
    showUpgradeModal(
      { commit },
      { upgradeStep, makersCount, wallsUsed }: { upgradeStep: UpgradeStep; makersCount: number; wallsUsed: number },
    ): void {
      commit('SET_MAKERS_COUNT', makersCount)
      commit('SET_WALLS_USED', wallsUsed)
      commit('SHOW_UPGRADE_MODAL', upgradeStep)
    },
    showUpgradeToTeamGoldModal(
      { dispatch },
      { makersCount, wallsUsed }: { makersCount: number; wallsUsed: number },
    ): void {
      dispatch('showUpgradeModal', { upgradeStep: UpgradeStep.ScheduleTeamGold, makersCount, wallsUsed })
    },
    closeUpgradeModal({ commit, dispatch }): void {
      commit('CLOSE_UPGRADE_MODAL')
      dispatch('resetUpgradeModalState')
    },
    closeUpgradeModalAndResetPlans({ commit, dispatch }): void {
      dispatch('closeUpgradeModal')
      commit('RESET_PLANS')
    },
    resetUpgradeModalState({ commit }): void {
      commit('SET_MAKERS_COUNT', null)
      commit('SET_WALLS_USED', null)
    },
    changeUpgradeModalStep({ commit }, { upgradeStep }: { upgradeStep: UpgradeStep }): void {
      commit('SET_UPGRADE_MODAL_STEP', upgradeStep)
    },
  },
}

export default LibraryPlansModule
export { UpgradeStep }
export type { HashedLibraryPlans }
